import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {ArrowDown, ArrowUp, ArrowUpDown, ChevronDown} from "lucide-react";
import moment from "moment";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {toLocalDateStr} from "../../../utils/dateHelpers";
import {fundMovementCategoryLabel} from "../fundMovementCategories";

// Onglet « Mouvements » de l'Activité : mouvements de fonds (entrées/sorties
// de caisse) regroupés par catégorie — laquelle en génère le plus, triable
// sur chaque colonne — et, au clic sur une catégorie, la liste de ses
// mouvements. API : /activite/fund_movements_totals (agrégats, annulés
// exclus) et /activite/fund_movements_by_category (détail, annulés inclus
// et marqués).

const COLUMNS = [
    {key: "category", align: "left"},
    {key: "count", align: "right"},
    {key: "share", align: "left", sortable: false},
    {key: "entreesTotal", align: "right"},
    {key: "sortiesTotal", align: "right"},
    {key: "total", align: "right"},
];

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("fr-FR", {minimumFractionDigits: 2, maximumFractionDigits: 2}) + " DH";

const FundMovementsByCategory = ({_dateRange, _startTime, _endTime, isDarkMode = false, refreshKey = 0}) => {
    const {t} = useTranslation("activite");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState(""); // "" | "entree" | "sortie"
    // Par défaut : la catégorie qui génère le PLUS de mouvements en premier.
    const [sort, setSort] = useState({key: "count", dir: "desc"});
    const [openCategory, setOpenCategory] = useState(undefined); // categoryKey (null = sans catégorie)
    const [details, setDetails] = useState({}); // categoryKey → {loading, items}

    const buildQuery = (extra = {}) => {
        const params = new URLSearchParams({
            startDate: toLocalDateStr(_dateRange[0].startDate),
            endDate: toLocalDateStr(_dateRange[0].endDate),
            startTime: _startTime || "00:00",
            endTime: _endTime || "23:59",
            ...(type ? {type} : {}),
            ...extra,
        });
        return params.toString();
    };

    const getHeaders = async () => {
        const account = await secureStorage.getItem("selectedAccount");
        return {"X-account-id": account.id, "Accept": "application/json"};
    };

    useEffect(() => {
        if (!_dateRange?.[0]) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setOpenCategory(undefined);
            setDetails({});
            try {
                const res = await api.get(`/activite/fund_movements_totals?${buildQuery()}`, {headers: await getHeaders()});
                if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error("Error fetching fund movements totals:", err);
                if (!cancelled) setRows([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_dateRange, _startTime, _endTime, refreshKey, type]);

    const totalCount = useMemo(() => rows.reduce((s, r) => s + (r.count || 0), 0), [rows]);

    const sortedRows = useMemo(() => {
        const withLabel = rows.map((r) => ({
            ...r,
            categoryKey: r.categoryKey ?? null,
            label: fundMovementCategoryLabel(t, r.categoryKey ?? r.category),
        }));
        const value = (r) => {
            if (sort.key === "category") return r.label.toLowerCase();
            // Sorties sont négatives : trier sur la valeur absolue pour que
            // « décroissant » = « plus grosses sorties d'abord ».
            if (sort.key === "sortiesTotal" || sort.key === "total") return Math.abs(r[sort.key] || 0);
            return r[sort.key] || 0;
        };
        return withLabel.sort((a, b) => {
            const va = value(a);
            const vb = value(b);
            const cmp = typeof va === "string" ? va.localeCompare(vb, "fr") : va - vb;
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [rows, sort, t]);

    const toggleSort = (key) => {
        setSort((prev) => (prev.key === key
            ? {key, dir: prev.dir === "asc" ? "desc" : "asc"}
            : {key, dir: key === "category" ? "asc" : "desc"}));
    };

    const toggleCategory = async (categoryKey) => {
        if (openCategory === categoryKey) {
            setOpenCategory(undefined);
            return;
        }
        setOpenCategory(categoryKey);
        const cacheKey = String(categoryKey);
        if (details[cacheKey]) return;
        setDetails((d) => ({...d, [cacheKey]: {loading: true, items: []}}));
        try {
            const res = await api.get(
                `/activite/fund_movements_by_category?${buildQuery({category: categoryKey ?? ""})}`,
                {headers: await getHeaders()}
            );
            setDetails((d) => ({...d, [cacheKey]: {loading: false, items: Array.isArray(res.data) ? res.data : []}}));
        } catch (err) {
            console.error("Error fetching fund movements by category:", err);
            setDetails((d) => ({...d, [cacheKey]: {loading: false, items: [], error: true}}));
        }
    };

    const top = sortedRows.length ? [...sortedRows].sort((a, b) => b.count - a.count)[0] : null;
    const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
    const border = isDarkMode ? "border-gray-700" : "border-gray-200";
    const amountClass = (v) => (v >= 0
        ? (isDarkMode ? "text-emerald-400" : "text-emerald-700")
        : (isDarkMode ? "text-red-400" : "text-red-700"));

    const renderDetails = (categoryKey) => {
        const entry = details[String(categoryKey)];
        if (!entry || entry.loading) {
            return <p className={`text-xs py-3 px-4 ${muted}`}>{t("expandableTable.loading")}</p>;
        }
        if (entry.error || entry.items.length === 0) {
            return <p className={`text-xs py-3 px-4 ${muted}`}>{t("fundMovementsByCategory.noMovements")}</p>;
        }
        return (
            <div className={`divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}>
                {entry.items.map((m) => (
                    <div key={m.id}
                         className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-4 py-2 text-xs sm:text-sm ${m.cancelled ? "opacity-60" : ""}`}>
                        <span className={`sm:w-36 flex-shrink-0 whitespace-nowrap ${muted}`}>
                            {m.createdAt ? moment(m.createdAt).format("DD/MM/YYYY HH:mm") : "—"}
                        </span>
                        <span className={`flex-1 min-w-0 truncate ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                            {m.motif || "—"}
                            {m.cancelled && (
                                <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${isDarkMode ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-700"}`}>
                                    {t("fundMovementCategories.cancelled")}
                                </span>
                            )}
                        </span>
                        <span className={`sm:w-40 flex-shrink-0 truncate ${muted}`}>
                            {[m.employee, m.device].filter(Boolean).join(" · ") || "—"}
                        </span>
                        <span className={`sm:w-28 flex-shrink-0 sm:text-right font-semibold whitespace-nowrap ${amountClass(m.total)} ${m.cancelled ? "line-through" : ""}`}>
                            {m.total >= 0 ? "+" : "−"}{formatCurrency(Math.abs(m.total))}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="mt-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <div className="flex gap-1">
                    {[
                        {value: "", label: t("fundMovementsByCategory.all")},
                        {value: "entree", label: t("expandableTable.columns.in")},
                        {value: "sortie", label: t("expandableTable.columns.out")},
                    ].map((opt) => (
                        <button
                            key={opt.value || "all"}
                            type="button"
                            onClick={() => setType(opt.value)}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                type === opt.value
                                    ? "bg-primary text-white"
                                    : isDarkMode
                                        ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                {top && !loading && (
                    <p className={`text-xs ${muted}`}>
                        {t("fundMovementsByCategory.topCategory")}{" "}
                        <span className={`font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}>{top.label}</span>
                        {" · "}
                        {t("fundMovementsByCategory.movementsShare", {
                            count: top.count,
                            percent: totalCount ? Math.round((top.count / totalCount) * 100) : 0,
                        })}
                    </p>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full mr-2"/>
                    <p className={`text-sm ${muted}`}>{t("expandableTable.loading")}</p>
                </div>
            ) : sortedRows.length === 0 ? (
                <div className="flex justify-center items-center py-8">
                    <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("expandableTable.noData")}</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                        <tr>
                            {COLUMNS.map((col) => {
                                const active = sort.key === col.key;
                                const SortIcon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
                                return (
                                    <th key={col.key}
                                        className={`py-2 px-4 text-xs font-medium border-b whitespace-nowrap ${border} ${muted} ${col.align === "right" ? "text-right" : "text-left"}`}>
                                        {col.sortable === false ? (
                                            t(`fundMovementsByCategory.columns.${col.key}`)
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => toggleSort(col.key)}
                                                className={`inline-flex items-center gap-1 hover:text-primary ${active ? "text-primary" : ""}`}
                                            >
                                                {t(`fundMovementsByCategory.columns.${col.key}`)}
                                                <SortIcon size={12}/>
                                            </button>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>
                        <tbody>
                        {sortedRows.map((r) => {
                            const isOpen = openCategory === r.categoryKey;
                            const share = totalCount ? (r.count / totalCount) * 100 : 0;
                            return (
                                <React.Fragment key={String(r.categoryKey)}>
                                    <tr
                                        onClick={() => toggleCategory(r.categoryKey)}
                                        className={`cursor-pointer transition-colors ${isOpen ? "bg-primary/5" : ""} ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
                                    >
                                        <td className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                                            <span className="inline-flex items-center gap-2">
                                                <ChevronDown size={14}
                                                             className={`flex-shrink-0 transition-transform duration-200 ${muted} ${isOpen ? "" : "-rotate-90"}`}/>
                                                {r.label}
                                            </span>
                                        </td>
                                        <td className={`py-3 px-4 text-sm text-right font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{r.count}</td>
                                        <td className="py-3 px-4 min-w-[120px]">
                                            <div className="flex items-center gap-2">
                                                <div className={`flex-1 h-1.5 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}>
                                                    <div className="h-1.5 rounded-full bg-primary" style={{width: `${Math.min(share, 100)}%`}}/>
                                                </div>
                                                <span className={`text-xs w-10 text-right ${muted}`}>{share.toFixed(0)}%</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right font-medium text-green-600 dark:text-green-400 whitespace-nowrap">
                                            {r.entreesTotal ? formatCurrency(r.entreesTotal) : "—"}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right font-medium text-red-600 dark:text-red-400 whitespace-nowrap">
                                            {r.sortiesTotal ? formatCurrency(r.sortiesTotal) : "—"}
                                        </td>
                                        <td className={`py-3 px-4 text-sm text-right font-semibold whitespace-nowrap ${amountClass(r.total)}`}>
                                            {formatCurrency(r.total)}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr>
                                            <td colSpan={COLUMNS.length} className={`p-0 border-y ${border} ${isDarkMode ? "bg-gray-900/40" : "bg-gray-50/70"}`}>
                                                {renderDetails(r.categoryKey)}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default FundMovementsByCategory;
